import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";

/**
 * Full-screen blocking modal shown to a team member whose `must_change_password`
 * flag is true. They can't dismiss it — the only action is to set their own
 * password (and optionally log out).
 */
export default function ChangePasswordModal() {
  const { refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const cur = current.trim();
    const nxt = next.trim();
    const cnf = confirm.trim();
    if (nxt.length < 4) {
      setErr("New password must be at least 4 characters");
      return;
    }
    if (nxt !== cnf) {
      setErr("New password and confirmation do not match");
      return;
    }
    if (nxt === cur) {
      setErr("New password must be different from the temporary one");
      return;
    }
    if (!acceptTerms) {
      setErr("Please accept the Terms of Service and Privacy Policy to continue");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: nxt,
        accept_terms: true,
      });
      toast.success("Password updated — you're all set");
      await refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not update password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4"
      data-testid="change-password-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white border border-gray-300 max-w-md w-full p-7 rounded-sm shadow-2xl"
      >
        <div className="flex items-start gap-3 mb-5">
          <div className="bg-[#002FA7] text-white p-2 rounded-sm">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-black text-xl tracking-tight">Set your password</h4>
            <p className="text-xs text-gray-500 mt-1">
              Welcome aboard. Before you start, please replace the temporary
              password your admin shared with one only you know.
            </p>
          </div>
        </div>

        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
          Temporary password
        </label>
        <input
          required
          type="text"
          autoComplete="current-password"
          autoCapitalize="off"
          spellCheck={false}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          data-testid="cp-current-input"
          className="w-full border border-gray-300 px-3 py-2 mb-3 rounded-sm text-sm font-mono"
        />

        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
          New password
        </label>
        <input
          required
          type="text"
          minLength={4}
          autoComplete="new-password"
          autoCapitalize="off"
          spellCheck={false}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          data-testid="cp-new-input"
          className="w-full border border-gray-300 px-3 py-2 mb-3 rounded-sm text-sm font-mono"
        />

        <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
          Confirm new password
        </label>
        <input
          required
          type="text"
          minLength={4}
          autoComplete="new-password"
          autoCapitalize="off"
          spellCheck={false}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="cp-confirm-input"
          className="w-full border border-gray-300 px-3 py-2 mb-4 rounded-sm text-sm font-mono"
        />

        {err && (
          <div
            className="mb-4 border-l-4 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-900"
            data-testid="cp-error"
          >
            {err}
          </div>
        )}

        <label className="flex items-start gap-2 mb-4 cursor-pointer text-xs text-gray-700">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            data-testid="cp-accept-terms"
            className="mt-0.5"
          />
          <span>
            I agree to the{" "}
            <Link
              to="/terms"
              target="_blank"
              className="text-[#002FA7] hover:underline font-semibold"
              data-testid="cp-terms-link"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy"
              target="_blank"
              className="text-[#002FA7] hover:underline font-semibold"
              data-testid="cp-privacy-link"
            >
              Privacy Policy
            </Link>
            .
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          data-testid="cp-submit"
          className="w-full bg-[#002FA7] text-white py-2.5 font-bold rounded-sm hover:bg-[#002277] disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          UPDATE PASSWORD
        </button>

        <button
          type="button"
          onClick={logout}
          data-testid="cp-logout"
          className="w-full mt-3 text-xs text-gray-500 hover:text-gray-900"
        >
          Sign out instead
        </button>
      </form>
    </div>
  );
}
